import { Box, Button, Link } from '@bcp-nextgen-dx-component-factory/design-system';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { AppSegmentedInput, Captcha, CollapsibleBlock, InvalidDataAlert } from '@components';
import { UIHelper } from '@helpers';
import { useLoginForm, useTranslation } from '@hooks';
import { useServicesStore } from '@store';
import { SafeCredentialTypeEnum } from '@types';

const resources = {
	title: 'screens.login.form.taxNumber.title',
	tooltip: {
		title: 'screens.login.form.taxNumber.tooltip.title',
		description: 'screens.login.form.taxNumber.tooltip.description',
	},
	button: {
		continue: 'screens.login.button.continue',
	},
	link: {
		readMode: 'screens.login.link.readMode',
	},
};

export interface TaxNumberBlockProps {
	isActive?: boolean;
	onSubmit?: (withCertificate?: boolean, safeCredentialType?: SafeCredentialTypeEnum) => void;
}

export const TaxNumberBlock = ({ isActive, onSubmit }: TaxNumberBlockProps) => {
	const { t } = useTranslation();
	const { positionsToAsk, needsCertificate } = useServicesStore((state) => state.accountLogin) || {};
	const { errorMessage } = useServicesStore((state) => state.accountChallenge) || {};

	const { taxNumber, password, showCaptcha, captchaValue, onContinueClick, setState } = useLoginForm();

	const handleSegmentedInputOnChange = (value?: string) => {
		setState({ form: { taxNumber: value || '' } });
	};

	const handleCaptchaChange = (value: string) => {
		setState({ form: { captchaValue: value } });
	};

	const taxNumberValidation = !taxNumber || taxNumber.length < (positionsToAsk?.length || 0);
	const passwordValidation = !password || password.length < 6;
	const captchaValidation = showCaptcha && !captchaValue;

	const openContentButtonDisabled = taxNumberValidation || passwordValidation || captchaValidation;

	const openContentChildren = (
		<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(32) }}>
			<AppSegmentedInput
				autoFocus={!!errorMessage ? false : true}
				length={positionsToAsk?.length || 0}
				mask="alphanumeric"
				inputMode="text"
				secureTextEntry
				renderLabel={(index) => `${positionsToAsk?.[index] || ''}ª`}
				value={taxNumber || ''}
				onChange={handleSegmentedInputOnChange}
			/>

			<InvalidDataAlert />

			{showCaptcha && (
				<Box styles={{ marginTop: UIHelper.rem(32) }}>
					<Captcha value={captchaValue} onChange={handleCaptchaChange} />
				</Box>
			)}

			<Box styles={{ alignSelf: 'start' }}>
				<Button
					type="submit"
					size="large"
					endContent={ArrowRight.Outline}
					disabled={openContentButtonDisabled}
					onPress={() => onContinueClick(onSubmit, { withCertificate: !!needsCertificate })}
				>
					{t(resources.button.continue)}
				</Button>
			</Box>

			{needsCertificate && (
				<Box>
					<Link
						label={t(resources.link.readMode)}
						disabled={openContentButtonDisabled}
						onPress={() => !openContentButtonDisabled && onContinueClick(onSubmit)}
					/>
				</Box>
			)}
		</Box>
	);

	return (
		<CollapsibleBlock
			isOpen={isActive}
			openContent={{
				label: {
					value: t(resources.title),
					tooltip: {
						title: t(resources.tooltip.title),
						description: t(resources.tooltip.description),
					},
				},
				children: openContentChildren,
			}}
		/>
	);
};
